/**
 * Multi-value variant of AsyncSearchSelect — same async-fetch/pagination hook,
 * but backed by react-select's isMulti mode for columns that can hold 2+ values
 * (e.g. multiple grades for one manufacturer).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import type { CSSObjectWithLabel, MenuListProps } from 'react-select';
import { SelectOption } from '../../shared/utils/asyncSelectApi';
import { useAsyncSelect } from '../../shared/hooks/useAsyncSelect';
import { GRAY_200, GRAY_300, GRAY_500 } from 'theme/grayScale';

interface AsyncMultiSearchSelectProps {
  value: string[];
  onChange: (values: string[]) => void;
  apiEndpoint: string;
  searchField: string;
  valueField: string;
  labelField: string;
  placeholder?: string;
  disabled?: boolean;
  /** Pre-populate the displayed labels immediately without waiting for async fetch. Parallel to value. */
  initialLabels?: string[];
}

const AsyncMultiSearchSelect: React.FC<AsyncMultiSearchSelectProps> = ({
  value,
  onChange,
  apiEndpoint,
  searchField,
  valueField,
  labelField,
  placeholder = 'Select value',
  disabled = false,
  initialLabels,
}) => {
  const {
    options,
    loadOptions,
    handleMenuScrollToBottom,
    scrollContainerRef,
    fetchSelectedValue,
    isLoadingMore,
  } = useAsyncSelect({
    apiEndpoint,
    searchField,
    valueField,
    labelField,
  });

  const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>(
    value.length && initialLabels && initialLabels.length === value.length
      ? value.map((v, i) => ({ value: v, label: initialLabels[i] }))
      : []
  );
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    loadOptions(inputValue, () => {
      setIsLoading(false);
    });
  }, [inputValue, loadOptions]);

  // Read via a ref (not a dependency) so this effect only re-syncs when the
  // external `value` prop changes, not on our own onChange-driven local
  // updates — including selectedOptions as a dependency here caused an
  // infinite render loop (empty-state reset -> new [] reference -> dependency
  // "changed" -> effect refires -> reset again) and, separately, wiped a
  // just-made selection before the parent's onChange round-trip landed.
  const selectedOptionsRef = useRef(selectedOptions);
  useEffect(() => {
    selectedOptionsRef.current = selectedOptions;
  }, [selectedOptions]);

  useEffect(() => {
    const loadValues = async () => {
      const currentOptions = selectedOptionsRef.current;

      if (!value.length) {
        if (currentOptions.length > 0) {
          setSelectedOptions([]);
        }
        return;
      }

      const selectedValues = new Set(currentOptions.map(o => o.value));
      const sameLength = currentOptions.length === value.length;
      const sameMembers = value.every(v => selectedValues.has(v));
      if (sameLength && sameMembers) {
        return;
      }

      try {
        const resolved = await Promise.all(
          value.map(async v => {
            const existing = currentOptions.find(o => o.value === v);
            if (existing) return existing;
            const option = await fetchSelectedValue(v);
            return option || { label: v, value: String(v) };
          })
        );
        setSelectedOptions(resolved);
      } catch (error) {
        console.error('Error loading selected values:', error);
        setSelectedOptions(value.map(v => ({ label: v, value: String(v) })));
      }
    };

    loadValues();
  }, [value, fetchSelectedValue]);

  const MenuList = (props: MenuListProps<SelectOption, true>) => {
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const target = event.target as HTMLDivElement;

      const bottom =
        target.scrollHeight - target.scrollTop <= target.clientHeight + 50;

      if (bottom && !isLoadingMore) {
        handleMenuScrollToBottom();
      }
    };

    const combinedRef = (element: HTMLDivElement | null) => {
      scrollContainerRef(element);
      if (props.innerRef) {
        if (typeof props.innerRef === 'function') {
          props.innerRef(element);
        } else {
          (
            props.innerRef as React.MutableRefObject<HTMLDivElement | null>
          ).current = element;
        }
      }
    };

    return (
      <>
        <components.MenuList
          {...props}
          innerRef={combinedRef}
          innerProps={{
            ...props.innerProps,
            onScroll: handleScroll,
          }}>
          {props.children}
        </components.MenuList>
        {isLoadingMore && (
          <div
            style={{
              padding: '8px 12px',
              textAlign: 'center',
              color: GRAY_500,
              fontSize: '0.875rem',
              borderTop: `1px solid ${GRAY_200}`,
            }}>
            ⏳ Loading more...
          </div>
        )}
      </>
    );
  };

  const selectStyles = useMemo(
    () => ({
      control: (base: CSSObjectWithLabel) => ({
        ...base,
        minHeight: '34px',
        fontSize: '0.875rem',
        borderColor: GRAY_300,
      }),
      option: (base: CSSObjectWithLabel) => ({
        ...base,
        fontSize: '0.9375rem',
      }),
      multiValue: (base: CSSObjectWithLabel) => ({
        ...base,
        fontSize: '0.875rem',
      }),
      placeholder: (base: CSSObjectWithLabel) => ({
        ...base,
        fontSize: '0.875rem',
      }),
      input: (base: CSSObjectWithLabel) => ({
        ...base,
        fontSize: '0.875rem',
      }),
      menuPortal: (base: CSSObjectWithLabel) => ({
        ...base,
        // Must exceed the ProductModal overlay (z-index: 10000)
        zIndex: 10001,
      }),
      loadingMessage: (base: CSSObjectWithLabel) => ({
        ...base,
        padding: '8px 12px',
        color: GRAY_500,
        fontSize: '0.875rem',
      }),
    }),
    []
  );

  return (
    <Select
      isMulti
      value={selectedOptions}
      onChange={selected => {
        const options = (selected as SelectOption[] | null) || [];
        setSelectedOptions(options);
        onChange(options.map(o => o.value));
      }}
      onInputChange={(newValue, actionMeta) => {
        if (actionMeta.action === 'input-change') {
          setInputValue(newValue);
        }
      }}
      options={options}
      components={{ MenuList }}
      isClearable
      isLoading={isLoading}
      isDisabled={disabled}
      placeholder={placeholder}
      menuPortalTarget={document.body}
      styles={selectStyles}
      filterOption={() => true}
      aria-label={placeholder}
      aria-invalid={false}
      aria-describedby={undefined}
      classNamePrefix="async-multi-select"
    />
  );
};

export default React.memo(AsyncMultiSearchSelect);
